import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Container,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { TableCellType } from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  BaseFilter,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";

const SeriesList = () => {
  const { formatMessage: t } = useIntl();
  const { seriesService, seriesDetailsUrl } = useGlobalContext();
  const navigate = useNavigate();
  const searchTerm = persistanceStore.useState((s) => s.seriesFilterSearchTerm);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const { organizationId } = contextStore.useState();

  const { isError, data, error, isFetching } = useQuery(
    ["series", organizationId, debouncedSearchTerm],
    () =>
      seriesService.getSeries(
        ListRequestOfBaseFilter.fromJS({
          pageSize: -1,
          parameters: BaseFilter.fromJS({
            searchText: debouncedSearchTerm,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  useListTitle("menubarSeries");

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "sku",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuLabel" }),
    },
    {
      accessor: "seriesTitle",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "seriesLabel" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(seriesDetailsUrl(id));
  };

  return (
    <Container>
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),

            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    persistanceStore.update((s) => {
                      s.seriesFilterSearchTerm = "";
                    })
                  }
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            persistanceStore.update((s) => {
              s.seriesFilterSearchTerm = e.target.value;
            });
          }}
        />
      </Box>

      <SortableTable
        {...{
          rows: data?.data ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "menubarSeries" }),
          cells,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => seriesDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default SeriesList;
